/**
 * Word Service
 * 
 * Business logic for word-level operations in transcript blocks
 */

import { CONFIG } from '../../components/TranscriptEditor/constants/config';

/**
 * Word Service Class
 */
class WordService {
  /**
   * Find word that matches selected text
   * Uses multiple strategies: exact match, partial match, range-based match
   * @param {Object} block - Block containing words
   * @param {string} selectedText - Selected text to match
   * @param {Range|null} range - Selection range (optional)
   * @returns {Object|null} Found word with index, or null
   */
  static findMatchingWord(block, selectedText, range = null) {
    if (!block || !block.words || block.words.length === 0) {
      return null;
    }

    const normalizedSelectedText = selectedText.trim().toLowerCase();
    
    // Strategy 1: Direct exact match (case-insensitive)
    for (let i = 0; i < block.words.length; i++) {
      const word = block.words[i];
      const wordText = (word.word || word.raw || '').trim().toLowerCase();
      
      if (wordText === normalizedSelectedText) {
        return { word, index: i };
      }
    }
    
    // Strategy 2: Partial match (only if selection is long enough)
    if (normalizedSelectedText.length >= CONFIG.MIN_WORD_LENGTH_FOR_PARTIAL_MATCH) {
      for (let i = 0; i < block.words.length; i++) {
        const word = block.words[i];
        const wordText = (word.word || word.raw || '').trim().toLowerCase();
        
        // Only match if word is similar length (prevent false matches)
        if (wordText.length >= CONFIG.MIN_WORD_LENGTH_FOR_PARTIAL_MATCH && (
          wordText.includes(normalizedSelectedText) || 
          (normalizedSelectedText.includes(wordText) && 
           Math.abs(wordText.length - normalizedSelectedText.length) <= CONFIG.MAX_WORD_LENGTH_DIFF)
        )) {
          return { word, index: i };
        }
      }
    }
    
    // Strategy 3: Use Range API to get exact selection position
    if (range) {
      try {
        const startContainer = range.startContainer;
        const startOffset = range.startOffset;
        
        // Reconstruct text and find which word contains this offset
        let charCount = 0;
        for (let i = 0; i < block.words.length; i++) {
          const word = block.words[i];
          const wordText = (word.word || word.raw || '').trim();
          const wordStart = charCount;
          const wordEnd = charCount + wordText.length;
          
          charCount += wordText.length + 1; // +1 for space
          
          // Check if selection starts within this word
          if (startOffset >= wordStart && startOffset <= wordEnd) {
            return { word, index: i };
          }
        }
      } catch (error) {
        // Range-based search failed, continue to return null
      }
    }
    
    return null;
  }

  /**
   * Get word text from word object
   * @param {Object} word - Word object
   * @returns {string} Word text
   */
  static getWordText(word) {
    return (word.word || word.raw || '').trim();
  }

  /**
   * Check if block has word-level data
   * @param {Object} block - Block to check
   * @returns {boolean} True if block has words
   */
  static hasWords(block) {
    return block && block.words && Array.isArray(block.words) && block.words.length > 0;
  }

  /**
   * Find active word index based on current time
   * @param {Object} block - Block containing words
   * @param {number} currentTime - Current playback time
   * @returns {number} Word index or -1 if not found
   */
  static findActiveWordIndex(block, currentTime) {
    if (!this.hasWords(block)) {
      return -1;
    }

    return block.words.findIndex(
      (word) => currentTime >= word.start && currentTime < word.end
    );
  }
}

export default WordService;

